import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Check, Download, Heart, ImageIcon, KeyRound, Loader2, X } from "lucide-react";
import { api, API, formatApiError } from "@/lib/api";

function Countdown({ expiresAt }) {
  const [now, setNow] = useState(Date.now());

  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);

  if (!expiresAt) return null;
  const diff = new Date(expiresAt).getTime() - now;
  if (diff <= 0) return null;

  const days = Math.floor(diff / 86400000);
  const hours = Math.floor((diff % 86400000) / 3600000);
  const mins = Math.floor((diff % 3600000) / 60000);
  const secs = Math.floor((diff % 60000) / 1000);
  const urgent = days < 7;

  const Cell = ({ value, label }) => (
    <div className="flex flex-col items-center px-4 py-3 sm:px-6">
      <span className={`font-serif text-2xl font-light tabular-nums sm:text-3xl ${urgent ? "text-terracotta" : "text-gold"}`}>
        {String(value).padStart(2, "0")}
      </span>
      <span className="mt-1 font-mono text-[8px] uppercase tracking-[0.25em] text-zinc-500">{label}</span>
    </div>
  );

  return (
    <div
      className={`flex items-center divide-x divide-white/10 border ${urgent ? "border-terracotta/40 bg-terracotta/5" : "border-gold/30 bg-gold/5"}`}
      data-testid="galerie-countdown"
    >
      <div className="flex divide-x divide-white/10">
        <Cell value={days} label="Tage" />
        <Cell value={hours} label="Std" />
        <Cell value={mins} label="Min" />
        <Cell value={secs} label="Sek" />
      </div>
      <p className="hidden px-5 font-mono text-[9px] uppercase leading-relaxed tracking-[0.2em] text-zinc-500 sm:block">
        {urgent ? "Galerie läuft bald ab — sichere deine Bilder rechtzeitig" : "So lange ist deine Galerie noch online"}
      </p>
    </div>
  );
}

export default function Galerie() {
  const [params] = useSearchParams();
  const [code, setCode] = useState(params.get("code") || "");
  const [gallery, setGallery] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [favs, setFavs] = useState({});
  const [noteDrafts, setNoteDrafts] = useState({});
  const [noteSaved, setNoteSaved] = useState({});
  const [bursts, setBursts] = useState({});
  const [fullPhoto, setFullPhoto] = useState(null);

  const loadFavorites = async (c) => {
    try {
      const res = await api.get(`/galerie/${c}/favorites`);
      const map = {};
      res.data.forEach((f) => (map[f.photo_id] = f.note || ""));
      setFavs(map);
      setNoteDrafts(map);
    } catch {
      /* Favoriten optional */
    }
  };

  const load = async (c) => {
    if (!c.trim()) return;
    setLoading(true);
    setError("");
    try {
      const res = await api.get(`/galerie/${c.trim().toUpperCase()}`);
      setGallery(res.data);
      setCode(c.trim().toUpperCase());
      loadFavorites(c.trim().toUpperCase());
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail, "Code nicht gefunden — bitte prüfe ihn noch einmal."));
    } finally {
      setLoading(false);
    }
  };

  const toggleFav = async (photoId) => {
    const c = code.trim().toUpperCase();
    if (favs[photoId] !== undefined) {
      setFavs((prev) => {
        const next = { ...prev };
        delete next[photoId];
        return next;
      });
      try {
        await api.delete(`/galerie/${c}/favorites/${photoId}`);
      } catch {
        /* stillschweigend */
      }
    } else {
      setFavs((prev) => ({ ...prev, [photoId]: "" }));
      setBursts((prev) => ({ ...prev, [photoId]: Date.now() }));
      setTimeout(() => setBursts((prev) => {
        const next = { ...prev };
        delete next[photoId];
        return next;
      }), 1100);
      try {
        await api.post(`/galerie/${c}/favorites`, { photo_id: photoId, note: "" });
      } catch {
        /* stillschweigend */
      }
    }
  };

  const saveNote = async (photoId) => {
    const c = code.trim().toUpperCase();
    try {
      const res = await api.post(`/galerie/${c}/favorites`, { photo_id: photoId, note: noteDrafts[photoId] || "" });
      setFavs((prev) => ({ ...prev, [photoId]: res.data.note || "" }));
      setNoteSaved((prev) => ({ ...prev, [photoId]: true }));
      setTimeout(() => setNoteSaved((prev) => ({ ...prev, [photoId]: false })), 2000);
    } catch {
      /* stillschweigend */
    }
  };

  const favCount = Object.keys(favs).length;

  useEffect(() => {
    const c = params.get("code");
    if (c) load(c);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="min-h-screen bg-ink text-white" data-testid="galerie-page">
      <header className="border-b border-white/10">
        <div className="mx-auto flex max-w-[1200px] items-center justify-between px-6 py-5">
          <Link to="/" className="flex items-center gap-3" data-testid="galerie-logo-link">
            <img src="/assets/logo-mark.png" alt="Leno Fotografie" className="h-9 w-9 rounded-full object-cover ring-1 ring-white/15" />
            <span className="font-sans text-lg font-semibold tracking-[0.18em]">LENO</span>
          </Link>
          <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-gold">Private Kunden-Galerie</span>
        </div>
      </header>

      {!gallery ? (
        <div className="flex min-h-[70vh] items-center justify-center px-6">
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-md border border-white/10 bg-ink2 p-10 text-center">
            <KeyRound size={28} className="mx-auto text-gold" />
            <h1 className="mt-6 font-serif text-3xl font-light">Deine Bilder warten auf dich</h1>
            <p className="mt-3 text-sm text-zinc-400">
              Gib den Zugangscode ein, den du per E-Mail erhalten hast.
            </p>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                load(code);
              }}
              className="mt-8"
              data-testid="galerie-code-form"
            >
              <input
                value={code}
                onChange={(e) => setCode(e.target.value.toUpperCase())}
                placeholder="Z. B. A1B2C3"
                data-testid="galerie-code-input"
                className="input-line text-center font-mono text-lg tracking-[0.4em] uppercase"
                maxLength={8}
              />
              {error && (
                <p className="mt-4 border border-terracotta/40 bg-terracotta/10 px-4 py-3 text-xs text-terracotta" data-testid="galerie-error">
                  {error}
                </p>
              )}
              <button
                type="submit"
                disabled={loading}
                data-testid="galerie-open-button"
                className="mt-6 flex w-full items-center justify-center gap-2 rounded-full bg-gold py-3.5 font-mono text-[11px] uppercase tracking-[0.25em] text-ink transition-colors hover:bg-white disabled:opacity-50"
              >
                {loading ? <Loader2 size={14} className="animate-spin" /> : <ImageIcon size={14} />}
                Galerie öffnen
              </button>
            </form>
          </motion.div>
        </div>
      ) : (
        <main className="mx-auto max-w-[1200px] px-6 py-14">
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} className="flex flex-col justify-between gap-6 lg:flex-row lg:items-end">
            <div>
              <p className="font-mono text-[10px] uppercase tracking-[0.35em] text-gold">Nur für dich, {gallery.client_name}</p>
              <h1 className="mt-3 font-serif text-4xl font-light tracking-tight" data-testid="galerie-title">
                {gallery.title}
              </h1>
              <p className="mt-2 text-sm text-zinc-400">{gallery.photos.length} Fotos — tippe auf ein Bild für volle Größe, der goldene Button speichert es in Originalqualität auf deinem Gerät</p>
              <p className="mt-3 flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.25em] text-gold" data-testid="galerie-fav-hint">
                <Heart size={12} className={favCount > 0 ? "fill-gold" : ""} />
                {favCount > 0
                  ? `${favCount} ${favCount === 1 ? "Lieblingsbild" : "Lieblingsbilder"} markiert`
                  : "Tippe auf das Herz, um deine Lieblingsbilder zu markieren"}
              </p>
              {gallery.welcome && (
                <p className="mt-5 max-w-xl border-l-2 border-gold pl-5 font-serif text-lg italic leading-relaxed text-zinc-300" data-testid="galerie-welcome">
                  {gallery.welcome}
                </p>
              )}
            </div>
            <a
              href={`${API}/galerie/${code}/download-all`}
              data-testid="galerie-download-all"
              className="flex w-fit items-center gap-3 rounded-full bg-gold px-7 py-3.5 font-mono text-[11px] uppercase tracking-[0.2em] text-ink transition-colors hover:bg-white"
            >
              <Download size={14} /> Alle als ZIP laden
            </a>
          </motion.div>

          {gallery.expires_at && (
            <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} className="mt-8">
              <Countdown expiresAt={gallery.expires_at} />
            </motion.div>
          )}

          <div className="mt-12 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {gallery.photos.map((p, i) => (
              <motion.figure
                key={p.id}
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: Math.min(i * 0.05, 0.6), duration: 0.6 }}
                className={`group relative overflow-hidden border bg-ink2 ${favs[p.id] !== undefined ? "border-gold/60" : "border-white/10"}`}
                data-testid={`galerie-photo-${p.id}`}
              >
                <button
                  onClick={() => setFullPhoto(p)}
                  data-testid={`galerie-view-${p.id}`}
                  aria-label="In Originalgröße ansehen"
                  className="block w-full cursor-zoom-in"
                  data-cursor="view"
                >
                  <img
                    src={`${API}/files/${p.storage_path}`}
                    alt={p.filename || `Foto ${i + 1}`}
                    loading="lazy"
                    decoding="async"
                    className="aspect-[3/2] w-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                </button>
                <button
                  onClick={() => toggleFav(p.id)}
                  data-testid={`galerie-fav-${p.id}`}
                  aria-label="Als Lieblingsbild markieren"
                  className={`absolute left-3 top-3 z-10 flex h-10 w-10 items-center justify-center rounded-full backdrop-blur transition-all duration-300 ${
                    favs[p.id] !== undefined
                      ? "bg-gold text-ink scale-110"
                      : "bg-ink/80 text-gold hover:bg-gold hover:text-ink"
                  }`}
                >
                  <Heart size={15} className={favs[p.id] !== undefined ? "fill-ink" : ""} />
                </button>
                {bursts[p.id] && (
                  <div className="pointer-events-none absolute inset-0 z-20 flex items-center justify-center" data-testid={`galerie-burst-${p.id}`}>
                    {[...Array(10)].map((_, i) => {
                      const angle = (i / 10) * Math.PI * 2 + 0.3;
                      const dist = 70 + (i % 3) * 30;
                      return (
                        <motion.span
                          key={`${bursts[p.id]}-${i}`}
                          initial={{ x: 0, y: 0, scale: 0.2, opacity: 1 }}
                          animate={{
                            x: Math.cos(angle) * dist,
                            y: Math.sin(angle) * dist - 24,
                            scale: [0.2, 1.1, 0.5],
                            opacity: [1, 1, 0],
                            rotate: (i % 2 ? 1 : -1) * 25,
                          }}
                          transition={{ duration: 0.95, ease: "easeOut" }}
                          className="absolute"
                        >
                          <Heart size={i % 3 === 0 ? 18 : 13} className="fill-gold text-gold drop-shadow-[0_2px_8px_rgba(212,175,55,0.6)]" />
                        </motion.span>
                      );
                    })}
                  </div>
                )}
                <a
                  href={`${API}/galerie/${code}/download/${p.id}`}
                  data-testid={`galerie-download-${p.id}`}
                  className="absolute bottom-3 right-3 flex h-10 w-10 items-center justify-center rounded-full bg-gold text-ink shadow-lg transition-all duration-300 hover:bg-white hover:scale-110"
                  aria-label="Foto herunterladen"
                  title="In Originalqualität speichern"
                >
                  <Download size={15} />
                </a>
                {favs[p.id] !== undefined && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: "auto" }}
                    className="border-t border-gold/30 bg-ink/95 p-3"
                    data-testid={`galerie-note-wrap-${p.id}`}
                  >
                    <div className="flex items-center gap-2">
                      <input
                        value={noteDrafts[p.id] || ""}
                        onChange={(e) => setNoteDrafts((prev) => ({ ...prev, [p.id]: e.target.value }))}
                        onKeyDown={(e) => e.key === "Enter" && saveNote(p.id)}
                        placeholder="Notiz für Eric, z. B. „Bitte als Druck!”"
                        maxLength={500}
                        data-testid={`galerie-note-input-${p.id}`}
                        className="w-full border-b border-white/15 bg-transparent pb-1.5 text-xs text-white placeholder:text-zinc-600 focus:border-gold focus:outline-none"
                      />
                      <button
                        onClick={() => saveNote(p.id)}
                        data-testid={`galerie-note-save-${p.id}`}
                        aria-label="Notiz speichern"
                        className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full transition-colors ${
                          noteSaved[p.id] ? "bg-emerald-500 text-ink" : "bg-gold text-ink hover:bg-white"
                        }`}
                      >
                        <Check size={13} />
                      </button>
                    </div>
                  </motion.div>
                )}
              </motion.figure>
            ))}
          </div>

          <p className="mt-14 border-t border-white/10 pt-8 text-center font-mono text-[9px] uppercase tracking-[0.25em] text-zinc-600">
            Mit Liebe gemacht — Leno Fotografie · lenofotografie.website
          </p>

          <AnimatePresence>
            {fullPhoto && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 z-50 flex flex-col items-center justify-center bg-ink/95 p-4 backdrop-blur-md"
                onClick={() => setFullPhoto(null)}
                data-testid="galerie-fullview"
              >
                <div className="absolute right-5 top-5 z-10 flex gap-3">
                  <a
                    href={`${API}/galerie/${code}/download/${fullPhoto.id}`}
                    data-testid="galerie-fullview-download"
                    className="flex h-11 items-center gap-2 rounded-full bg-gold px-5 font-mono text-[10px] uppercase tracking-[0.2em] text-ink transition-colors hover:bg-white"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <Download size={14} /> Speichern
                  </a>
                  <button
                    onClick={() => setFullPhoto(null)}
                    data-testid="galerie-fullview-close"
                    aria-label="Schließen"
                    className="flex h-11 w-11 items-center justify-center rounded-full border border-white/20 text-white transition-colors hover:border-gold hover:text-gold"
                  >
                    <X size={18} />
                  </button>
                </div>
                <motion.img
                  initial={{ scale: 0.96, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
                  src={`${API}/files/${fullPhoto.original_path || fullPhoto.storage_path}`}
                  alt={fullPhoto.filename || "Foto in Originalgröße"}
                  className="max-h-[82vh] w-auto max-w-full object-contain"
                  data-testid="galerie-fullview-image"
                  onClick={(e) => e.stopPropagation()}
                />
                <p className="mt-4 font-mono text-[9px] uppercase tracking-[0.25em] text-zinc-500">
                  Originalauflösung — zum Speichern oben rechts
                </p>
              </motion.div>
            )}
          </AnimatePresence>
        </main>
      )}
    </div>
  );
}
